import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ChevronIcon, XIcon } from '../components/icons';
import { GuideEmojiIcon, GuideSlugIcon } from '../components/guides/guide-icons';
import { useExpatGuideDetailQuery } from '../hooks/use-guides-query';
import type { ExpatGuideSlide } from '../types/expat-guides';

// Web port of mobile/src/screens/expat-guide-detail-screen.tsx +
// mobile/src/components/expat-guide-slide-carousel.tsx. Each guide
// renders as a horizontally-paged slide deck with the same content
// blocks: tag chip, visual, heading, body, facts grid, steps, tip,
// sources. Native scroll-snap drives the pager; arrow buttons + dots
// provide explicit navigation for desktop users.
export function GuideDetail() {
  const navigate = useNavigate();
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId ? Number(rawId) : null;
  const numericId = id != null && Number.isFinite(id) ? id : null;
  const { t } = useTranslation();

  const { data: topic, isLoading, isError } = useExpatGuideDetailQuery(numericId);

  // Paging state — we track which slide is "in view" by listening to
  // the scroll position of the horizontally-paged container.
  const railRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!railRef.current) return;
        const w = railRef.current.clientWidth;
        if (w <= 0) return;
        const idx = Math.round(railRef.current.scrollLeft / w);
        setActiveIndex(idx);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [topic?.id]);

  const accent = topic?.content.accent ?? '#FFFFFF';
  const accentDim = topic?.content.accentDim ?? 'rgba(255,255,255,0.10)';
  const grad = topic?.content.grad;
  const slides: readonly ExpatGuideSlide[] = useMemo(() => topic?.content.slides ?? [], [topic]);

  const goTo = (index: number) => {
    const el = railRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(slides.length - 1, index));
    el.scrollTo({ left: el.clientWidth * next, behavior: 'smooth' });
  };

  const containerStyle = grad
    ? ({
        background: `linear-gradient(180deg, ${grad[0]} 0%, ${grad[1]} 100%)`,
      } as const)
    : ({ background: topic?.content.bg ?? 'var(--bg)' } as const);

  if (isLoading) {
    return (
      <div className="nw-guide-detail" style={containerStyle}>
        <div className="nw-feed__loading" style={{ color: 'rgba(255,255,255,0.85)' }}>
          <div className="nw-feed__loading-spinner" aria-hidden />
          {t('web.guideDetail.loading')}
        </div>
      </div>
    );
  }
  if (isError || !topic) {
    return (
      <div className="nw-guide-detail" style={{ background: 'var(--bg)' }}>
        <div className="nw-feed__empty">{t('web.guideDetail.error')}</div>
        <button type="button" className="nw-btn nw-btn--ghost" onClick={() => navigate(-1)}>
          {t('web.guideDetail.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="nw-guide-detail" style={containerStyle}>
      <header className="nw-guide-detail__header">
        <div className="nw-guide-detail__brand">
          <span
            className="nw-guide-detail__icon-badge"
            style={{ background: accentDim, color: accent }}
            aria-hidden
          >
            <GuideSlugIcon slug={topic.slug} size={18} color={accent} />
          </span>
          <h1 className="nw-guide-detail__title">{topic.content.title}</h1>
        </div>
        <button
          type="button"
          className="nw-guide-detail__close"
          onClick={() => navigate(-1)}
          aria-label={t('web.guideDetail.a11y.close')}
        >
          <XIcon size={16} stroke={2.2} />
        </button>
      </header>

      <div className="nw-guide-detail__dots" role="tablist" aria-label={t('web.guideDetail.a11y.tablist')}>
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === activeIndex}
            aria-label={t('web.guideDetail.a11y.slideOf', { current: i + 1, total: slides.length })}
            className={`nw-guide-detail__dot ${i === activeIndex ? 'is-active' : ''}`}
            style={i === activeIndex ? { background: accent, width: 22 } : undefined}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      <div className="nw-guide-detail__pager" ref={railRef}>
        {slides.map((slide, i) => (
          <Slide key={i} slide={slide} accent={accent} accentDim={accentDim} />
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="nw-guide-detail__nav nw-guide-detail__nav--prev"
            onClick={() => goTo(activeIndex - 1)}
            disabled={activeIndex === 0}
            aria-label={t('web.guideDetail.a11y.previousSlide')}
          >
            <ChevronIcon dir="left" size={18} stroke={2.4} />
          </button>
          <button
            type="button"
            className="nw-guide-detail__nav nw-guide-detail__nav--next"
            onClick={() => goTo(activeIndex + 1)}
            disabled={activeIndex === slides.length - 1}
            aria-label={t('web.guideDetail.a11y.nextSlide')}
          >
            <ChevronIcon dir="right" size={18} stroke={2.4} />
          </button>
        </>
      )}
    </div>
  );
}

interface SlideProps {
  readonly slide: ExpatGuideSlide;
  readonly accent: string;
  readonly accentDim: string;
}

function Slide({ slide, accent, accentDim }: SlideProps) {
  const { t } = useTranslation();
  return (
    <article className="nw-guide-slide">
      <div className="nw-guide-slide__inner">
        <span
          className="nw-guide-slide__tag"
          style={{ background: accentDim, color: accent }}
        >
          {slide.tag}
        </span>

      <span className="nw-guide-slide__visual" aria-hidden>
        <GuideEmojiIcon emoji={slide.visual} size={44} color="#fff" />
      </span>

      <h2 className="nw-guide-slide__heading">{slide.heading}</h2>
      <p className="nw-guide-slide__body">{slide.body}</p>

      {slide.facts.length > 0 && (
        <div className="nw-guide-slide__facts">
          {slide.facts.map((fact, i) => (
            <div
              key={i}
              className="nw-guide-slide__fact"
              style={{ background: accentDim }}
            >
              <span className="nw-guide-slide__fact-icon" aria-hidden>
                <GuideEmojiIcon emoji={fact.icon} size={18} color="rgba(255,255,255,0.9)" />
              </span>
              <span className="nw-guide-slide__fact-label">{fact.label}</span>
              <span className="nw-guide-slide__fact-val" style={{ color: accent }}>
                {fact.val}
              </span>
            </div>
          ))}
        </div>
      )}

      {slide.steps.length > 0 && (
        <ol className="nw-guide-slide__steps">
          {slide.steps.map((step, i) => (
            <li key={i} className="nw-guide-slide__step">
              <span
                className="nw-guide-slide__step-num"
                style={{ background: accent }}
              >
                {i + 1}
              </span>
              <span className="nw-guide-slide__step-text">{step}</span>
            </li>
          ))}
        </ol>
      )}

      {slide.tip && (
        <aside
          className="nw-guide-slide__tip"
          style={{ borderColor: accent }}
        >
          <div className="nw-guide-slide__tip-label">{t('web.guideDetail.tip')}</div>
          <div className="nw-guide-slide__tip-text">{slide.tip}</div>
        </aside>
      )}

      {slide.sources.length > 0 && (
          <div className="nw-guide-slide__sources">
            {slide.sources.map((source, i) => (
              <a
                key={i}
                className="nw-guide-slide__source"
                href={source.url}
                target="_blank"
                rel="noreferrer"
                style={{ background: accentDim, color: accent }}
              >
                <span className="nw-guide-slide__source-label">{source.label}</span>
                <ChevronIcon dir="right" size={14} stroke={2.4} />
              </a>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
